import { FC, useState } from 'react';
import ReactDatePicker, { registerLocale } from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { cs } from 'date-fns/esm/locale';
import { dateHelper } from '../../../helpers/help_functions/date_helpers';
import styled from 'styled-components';
import { Colors, FontSize, Spacing } from '../../../assets/GlobalStyle/Variables';
import { FilterBar, FilterGroup, FilterLabel, FilterSelect, FilterButton, SecondaryButton } from './styles';
import type { FilterType, DateRange } from './useLabResults';

registerLocale('cz', cs);

type Props = {
  filterType: FilterType;
  dateRange: DateRange;
  onFilterTypeChange: (type: FilterType) => void;
  onDateRangeChange: (range: DateRange) => void;
};

export const LabResultsFilter: FC<Props> = ({
  filterType,
  dateRange,
  onFilterTypeChange,
  onDateRangeChange
}) => {
  const [dateFrom, setDateFrom] = useState<Date | null>(
    dateRange.from ? new Date(dateRange.from) : null
  );
  const [dateTo, setDateTo] = useState<Date | null>(dateRange.to ? new Date(dateRange.to) : null);

  const handleApply = () => {
    if (!dateFrom || !dateTo) return;
    onDateRangeChange({
      from: dateHelper.toISOLocal(dateFrom),
      to: dateHelper.toISOLocal(dateTo)
    });
  };

  const handleReset = () => {
    setDateFrom(null);
    setDateTo(null);
    onDateRangeChange({ from: '', to: '' });
  };

  const canApply =
    dateFrom instanceof Date &&
    !isNaN(dateFrom.getTime()) &&
    dateTo instanceof Date &&
    !isNaN(dateTo.getTime());

  return (
    <FilterBar>
      <FilterGroup>
        <FilterLabel htmlFor="filter-period">Časové období:</FilterLabel>
        <FilterSelect
          id="filter-period"
          value={filterType}
          onChange={(e) => onFilterTypeChange(e.target.value as FilterType)}>
          <option value="all">Všechny výsledky</option>
          <option value="current">Aktuální výsledky</option>
          <option value="last100">Posledních 100 výsledků</option>
          <option value="custom">Dle data</option>
        </FilterSelect>
      </FilterGroup>

      {filterType === 'custom' && (
        <>
          <FilterGroup>
            <FilterLabel>Datum od:</FilterLabel>
            <StyledDatePicker
              selected={dateFrom}
              onChange={(d) => setDateFrom(d as Date | null)}
              dateFormat="dd.MM.yyyy"
              placeholderText="dd.mm.rrrr"
              locale="cz"
              calendarStartDay={1}
              isClearable
            />
          </FilterGroup>

          <FilterGroup>
            <FilterLabel>Datum do:</FilterLabel>
            <StyledDatePicker
              selected={dateTo}
              onChange={(d) => setDateTo(d as Date | null)}
              dateFormat="dd.MM.yyyy"
              placeholderText="dd.mm.rrrr"
              locale="cz"
              calendarStartDay={1}
              isClearable
            />
          </FilterGroup>

          {(dateFrom || dateTo) && (
            <FilterGroup>
              <FilterLabel>&nbsp;</FilterLabel>
              <div style={{ display: 'flex', gap: '8px' }}>
                <SecondaryButton type="button" onClick={handleReset}>
                  Resetovat
                </SecondaryButton>
                {canApply && (
                  <FilterButton type="button" onClick={handleApply}>
                    Filtrovat
                  </FilterButton>
                )}
              </div>
            </FilterGroup>
          )}
        </>
      )}
    </FilterBar>
  );
};

const StyledDatePicker = styled(ReactDatePicker)`
  padding: ${Spacing(2)} ${Spacing(3)};
  border: 1px solid ${Colors.lightGrey};
  border-radius: 4px;
  font-size: ${FontSize.normal};
  color: ${Colors.mainText};
  background-color: ${Colors.white};
  width: 160px;

  &:focus {
    outline: 2px solid ${Colors.secondary};
    outline-offset: 1px;
  }
`;
